<script setup lang="ts">
import { nextTick, reactive, ref } from 'vue'
import { ApiError, changePassword } from '../api'

// The pupil's own password change. Plain HTML like the rest of the pupil path (no Quasar here):
// a native <dialog> with the same shape as AppDialog - small title, a close button on the right,
// a general error under the title, and each field error under its own field.
const props = defineProps<{ token: string }>()

const dialog = ref<HTMLDialogElement | null>(null)
const form = reactive({ current: '', next: '', again: '' })
const errors = reactive<Record<string, string>>({ current: '', next: '', again: '' })
const general = ref('')
const busy = ref(false)
const done = ref(false)

function clear(): void {
  for (const key of Object.keys(errors)) errors[key] = ''
  general.value = ''
}

function open(): void {
  form.current = form.next = form.again = ''
  done.value = false
  clear()
  dialog.value?.showModal()
  // The native dialog would focus its close button first; the cursor belongs in the first field.
  void nextTick(() => document.getElementById('pw-current')?.focus())
}

/** After a refused submit the first failed field gets the cursor (and scrolls into view). */
function focusFailed(): void {
  void nextTick(() => {
    const field = dialog.value?.querySelector<HTMLElement>('[aria-invalid="true"]')
    field?.scrollIntoView({ block: 'center' })
    field?.focus()
  })
}

function close(): void {
  if (!busy.value) dialog.value?.close()
}

async function submit(): Promise<void> {
  if (busy.value) return
  clear()
  if (!form.current) errors.current = 'Kata sandi saat ini wajib diisi.'
  if (!form.next) errors.next = 'Kata sandi baru wajib diisi.'
  else if (form.next.length < 4) errors.next = 'Kata sandi baru paling sedikit 4 karakter.'
  if (form.again !== form.next) errors.again = 'Pengulangan tidak sama dengan kata sandi baru.'
  if (errors.current || errors.next || errors.again) {
    focusFailed()
    return
  }
  busy.value = true
  try {
    await changePassword(props.token, form.current, form.next)
    done.value = true
  } catch (e) {
    if (e instanceof ApiError && e.fields.current) errors.current = e.fields.current
    else if (e instanceof ApiError && e.fields.new) errors.next = e.fields.new
    else general.value = e instanceof Error ? e.message : 'Kata sandi gagal diganti.'
    focusFailed()
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <button class="link-button" type="button" @click="open">Ganti kata sandi</button>

  <dialog ref="dialog" class="pw-dialog" aria-labelledby="pw-title">
    <header class="pw-head">
      <h3 id="pw-title">Ganti kata sandi</h3>
      <button class="pw-close" type="button" aria-label="Tutup" :disabled="busy" @click="close">×</button>
    </header>
    <p v-if="general" class="banner banner--error" role="alert">{{ general }}</p>

    <template v-if="done">
      <p class="banner banner--success" role="status">Kata sandi sudah diganti. Pakai yang baru saat masuk berikutnya.</p>
      <div class="pw-actions"><button class="button button--primary" type="button" @click="close">Selesai</button></div>
    </template>

    <form v-else novalidate @submit.prevent="submit">
      <div class="pw-field">
        <label for="pw-current">Kata sandi saat ini</label>
        <input id="pw-current" v-model="form.current" type="password" autocomplete="current-password" :aria-invalid="!!errors.current" @input="errors.current = ''" />
        <p v-if="errors.current" class="pw-error">{{ errors.current }}</p>
      </div>
      <div class="pw-field">
        <label for="pw-new">Kata sandi baru</label>
        <input id="pw-new" v-model="form.next" type="password" autocomplete="new-password" :aria-invalid="!!errors.next" @input="errors.next = ''" />
        <p v-if="errors.next" class="pw-error">{{ errors.next }}</p>
      </div>
      <div class="pw-field">
        <label for="pw-again">Ulangi kata sandi baru</label>
        <input id="pw-again" v-model="form.again" type="password" autocomplete="new-password" :aria-invalid="!!errors.again" @input="errors.again = ''" />
        <p v-if="errors.again" class="pw-error">{{ errors.again }}</p>
      </div>
      <div class="pw-actions">
        <button class="button button--secondary" type="button" :disabled="busy" @click="close">Batal</button>
        <button class="button button--primary" type="submit" :disabled="busy">{{ busy ? 'Menyimpan…' : 'Simpan' }}</button>
      </div>
    </form>
  </dialog>
</template>

<style scoped>
.pw-dialog {
  width: min(400px, 94vw);
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 12px;
  color: var(--ink);
}

.pw-dialog::backdrop {
  background: rgba(20, 33, 43, 0.4);
}

.pw-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.pw-head h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.pw-close {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}

.pw-field {
  margin: 8px 0;
}

.pw-field input[aria-invalid='true'] {
  border-color: #b3261e;
}

.pw-error {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: #b3261e;
}

.pw-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
</style>
